/**
 * A component that displays a tooltip when the user clicks or moves their mouse over label.
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import detectElementOverflow from 'detect-element-overflow';
import { noop } from 'utils/func';

export default class Tooltip extends PureComponent {
  static propTypes = {
    action: PropTypes.oneOf(['onClick', 'onMouseOver']),
    baseClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    defaultPositions: PropTypes.arrayOf(
      PropTypes.oneOf(['above', 'below', 'left', 'center', 'right', 'before', 'after', 'top', 'middle', 'bottom'])),
    label: PropTypes.node,
    forceShow: PropTypes.bool,
    autoHide: PropTypes.bool,
    autoHideDelay: PropTypes.number,
    hideCloseButton: PropTypes.bool,
    detectCollisions: PropTypes.bool,
    onClose: PropTypes.func,
  };

  static defaultProps = {
    action: 'onClick',
    // You can use Tooltip's logic for creating other components. Changing base class name might come in handy then.
    baseClassName: 'gn-tooltip',
    // Set a default tooltip position that will be used UNLESS a collision with container is detected.
    defaultPositions: ['above', 'center'],
    autoHideDelay: 5000,
    hideCloseButton: false,
    detectCollisions: true,
    onClose: noop,
  };

  state = {
    // We don't need to render dropdown content until the user activates it for the first time
    isRendered: false,
    isOpen: this.props.forceShow,
    forceShow: this.props.forceShow,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const shouldBeForcedToShow = nextProps.forceShow === true
      && prevState.forceShow === false;
    if (shouldBeForcedToShow) {
      return {
        ...prevState,
        isOpen: true,
      };
    }
    return null;
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.collapseIfClickedOutside);
    clearTimeout(this.autohideHandler);
  }

  get handlers() {
    const { action } = this.props;

    switch (action) {
      case 'onClick':
        return {
          onClick: this.toggle,
        };
      case 'onMouseOver':
        return {
          onMouseEnter: this.expandAfterDelay,
          onMouseLeave: this.collapseAfterDelay,
          onFocus: this.expand,
          onBlur: this.collapseIfFocusedOutside,
        };
      default: throw new Error(`Unknown action: ${action}.`);
    }
  }

  delayInMs = 400;

  expandAfterDelay = () => {
    if (this.toCollapse) {
      clearTimeout(this.toCollapse);
    }
    this.toExpand = setTimeout(this.expand, this.delayInMs);
  };

  collapseAfterDelay = () => {
    if (this.toExpand) {
      clearTimeout(this.toExpand);
    }
    this.toCollapse = setTimeout(this.collapse, this.delayInMs);
  };

  autohide = () => {
    this.setState({ isOpen: false });
  };

  expand = () => {
    this.setState({
      isRendered: true,
      isOpen: true,
    });

    document.addEventListener('click', this.collapseIfClickedOutside);
    if (this.props.autoHide) {
      this.autohideHandler = setTimeout(this.autohide, this.props.autoHideDelay);
    }
  };

  collapse = () => {
    this.setState({ isOpen: false });

    document.removeEventListener('click', this.collapseIfClickedOutside);
    this.props.onClose();
  };

  collapseIfClickedOutside = (event) => {
    event.stopPropagation();
    const clickedInside = this.container && this.container.contains(event.target);
    if (!clickedInside) {
      this.collapse();
    }
  };

  // If the user is focused of current dropdown, we need to collapse it
  collapseIfFocusedOutside = () => {
    setTimeout(() => {
      const stillHasFocus = this.container && this.container.querySelector(':focus, :hover');
      if (!stillHasFocus) {
        this.collapse();
      }
    }, 0);
  };

  toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return this.state.isOpen
      ? this.collapse()
      : this.expand();
  };

  detectCollisions = (ref) => {
    if (!ref) {
      return;
    }

    const { defaultPositions, detectCollisions } = this.props;

    if (!detectCollisions) {
      return;
    }

    const collisions = detectElementOverflow(ref, document.body);

    const methods = {
      above: () => (collisions.collidedTop ? 'below' : 'above'),
      below: () => (collisions.collidedBottom ? 'above' : 'below'),
      left: () => (collisions.collidedLeft ? 'left' : 'right'),
      right: () => (collisions.collidedRight ? 'right' : 'left'),
      before: () => (collisions.collidedLeft ? 'after' : 'before'),
      after: () => (collisions.collidedRight ? 'before' : 'after'),
      center() {
        if (collisions.collidedLeft) {
          return 'left';
        } else if (collisions.collidedRight) {
          return 'right';
        }
        return 'center';
      },
    };

    const calculatedPositions = defaultPositions.map(position => (methods[position] ? methods[position]() : position));

    ref.classList.remove(...defaultPositions);
    ref.classList.add(...calculatedPositions);
  };

  render() {
    const { handlers } = this;
    const {
      baseClassName,
      children,
      className,
      label,
      forceShow,
      autoHide,
      defaultPositions,
      hideCloseButton,
    } = this.props;
    const { isRendered, isOpen } = this.state;

    return (
      <div
        className={classNames(
          baseClassName,
          className,
          { [`${baseClassName}--open`]: isOpen },
          { [`${baseClassName}__autoHide`]: autoHide }
        )}
        {...handlers}
        ref={ref => { this.container = ref; }}
      >
        {label}
        {
          (forceShow || isRendered) && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions,jsx-a11y/no-noninteractive-element-interactions
            <section
              className={classNames(`${baseClassName}__content`, defaultPositions)}
              onClick={event => { event.stopPropagation(); }}
              ref={this.detectCollisions}
            >
              {!autoHide && !hideCloseButton && (
                <p className={`${baseClassName}__content__closeButton sg-icon`}>
                  <button
                    className="icon icon__close"
                    onClick={this.collapse}
                    aria-label="Close"
                  />
                </p>)}
              {children}
            </section>
          )}
      </div>
    );
  }
}
